import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { API } from "@/lib/api";
import AmbientMixer from "@/lib/ambientMixer";
import { setSessionMetadata, clearSessionMetadata, bindMediaSessionControls, attachAmbientStreamToAudio } from "@/lib/mediaSession";
import { startKeepAlive, stopKeepAlive } from "@/lib/silentKeepAlive";
import { retryOnNextGesture } from "@/lib/audioUnlock";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, Moon, Sun, Sparkles, BookmarkPlus, Volume2, VolumeX } from "lucide-react";
import { BottomNav, TopBar } from "@/components/AppShell";
import SettingsDrawer from "@/components/SettingsDrawer";

export default function SessionPlayer() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const replayMode = searchParams.get("replay") === "1";
  const [session, setSession] = useState(null);
  const [atmosphere, setAtmosphere] = useState(null);
  const [character, setCharacter] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioStatus, setAudioStatus] = useState("idle"); // idle | rendering | ready | failed
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [duration, setDuration] = useState(0);
  const [ambientEnabled, setAmbientEnabled] = useState(true);
  const [presenceMode, setPresenceMode] = useState(false);
  const audioRef = useRef(null);
  const mixerRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const fakeTickRef = useRef(null);
  const morningRampRef = useRef(null);
  const endFadeTimerRef = useRef(null);
  // Iter 118 — see WaitingRoom for the full lifecycle notes. In replay we
  // track user intent so an OS-driven pause (backgrounded tab, iOS
  // interruption) does NOT stop the mixer and CAN self-recover.
  const userInitiatedPauseRef = useRef(false);
  // Phase 3 — Hybrid Master Rendering. When present, playback uses ONE
  // deterministic MP3 (dialogue + ambient + events + silence, exactly matched
  // to session duration) instead of orchestrating live layers on replay.
  const [masterUrl, setMasterUrl] = useState(null);
  const [masterStatus, setMasterStatus] = useState(null); // null | generating | ready | failed
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Load session, atmosphere/character lookups, and manifest
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, atmos, chars] = await Promise.all([
          api.get(`/sessions/${id}`),
          api.get("/catalog/atmospheres"),
          api.get("/catalog/characters"),
        ]);
        if (cancelled) return;
        setSession(s.data);
        if (s.data.atmosphere_id && s.data.atmosphere_id !== "custom") {
          setAtmosphere(atmos.data.find((a) => a.id === s.data.atmosphere_id));
        } else {
          setAtmosphere({ name: s.data.atmosphere_name || "a quiet place", image: "https://images.pexels.com/photos/9323556/pexels-photo-9323556.jpeg" });
        }
        setCharacter(chars.data.find((c) => c.id === s.data.character_id));

        // Phase 3 — prefer the hybrid master track when available. It already
        // contains ambient + dialogue + events baked into one deterministic
        // MP3 exactly matched to the session's duration, so we do NOT spin
        // up an AmbientMixer or call /render_audio on replay.
        setMasterStatus(s.data.master_status || null);
        if (s.data.master_status === "ready" && s.data.master_url) {
          setMasterUrl(`${API}${s.data.master_url.replace("/api", "")}`);
          setAudioUrl(`${API}${s.data.master_url.replace("/api", "")}`);
          setAudioStatus("ready");
        } else if (replayMode && (s.data.master_status === "generating" || s.data.master_status == null)) {
          // Replay hit but master is still cooking — poll for it before
          // falling back to dialogue-only playback.
          setAudioStatus("rendering");
          const pollDeadline = Date.now() + 60000; // 60s replay-side patience
          while (Date.now() < pollDeadline) {
            await new Promise((res) => setTimeout(res, 2500));
            if (cancelled) return;
            try {
              const st = await api.get(`/sessions/${id}/master-status`);
              if (st.data?.ready && st.data?.url) {
                setMasterStatus("ready");
                setMasterUrl(`${API}${st.data.url.replace("/api", "")}`);
                setAudioUrl(`${API}${st.data.url.replace("/api", "")}`);
                setAudioStatus("ready");
                break;
              }
              if (st.data?.status === "failed" || st.data?.status === "error") {
                setMasterStatus("failed");
                break;
              }
            } catch (e) { /* transient — keep polling */ }
          }
        }

        const m = await api.get(`/sessions/${id}/manifest`);
        if (cancelled) return;
        setManifest(m.data);

        // Only fall through to per-layer dialogue TTS + AmbientMixer if the
        // hybrid master was NOT available (either it never ran or it failed).
        const usingMaster = !!(s.data.master_status === "ready" && s.data.master_url);
        if (!usingMaster && m.data.tts_configured) {
          if (m.data.layers.dialogue.ready && m.data.layers.dialogue.url) {
            setAudioUrl(`${API}${m.data.layers.dialogue.url.replace("/api", "")}`);
            setAudioStatus("ready");
          } else if (replayMode) {
            // Replay mode: don't burn TTS credits if cache is gone — show text only.
            setAudioStatus("failed");
          } else {
            setAudioStatus("rendering");
            try {
              const r = await api.post(`/sessions/${id}/render_audio`);
              if (cancelled) return;
              setAudioUrl(`${API}${r.data.url.replace("/api", "")}`);
              setAudioStatus("ready");
            } catch {
              if (cancelled) return;
              setAudioStatus("failed");
            }
          }
        }

        // Start ambient immediately so the place feels populated from the
        // moment the page mounts — no waiting, no white silence.
        // SKIP the ambient mixer entirely when playing the master track: it
        // already contains the exact ambient bed the user heard live.
        if (!usingMaster) {
          try {
            if (!mixerRef.current) mixerRef.current = new AmbientMixer();
            await mixerRef.current.start({
              environment: {
                synth: m.data.layers.environment.synth,
                environment_state: m.data.layers.environment.environment_state,
              },
              soundPlan: m.data.layers.sound_plan,
              environmentState: m.data.layers.environment.environment_state,
            });
            if (ambientAudioRef.current) {
              attachAmbientStreamToAudio(mixerRef.current, ambientAudioRef.current);
            }
            // Iter 69 — silent keep-alive to survive lock-screen suspend.
            try { startKeepAlive(); } catch (e) { /* ignore */ }
            if (m.data.layers.sound_plan?.ambient_events) {
              mixerRef.current.scheduleAmbientEvents(m.data.layers.sound_plan.ambient_events);
            } else if (m.data.layers.environment.tags?.length) {
              mixerRef.current.scheduleEvents(m.data.layers.environment.tags);
            }
          } catch (e) {
            console.warn("session: ambient failed to start", e);
          }
        }
      } catch {
        if (cancelled) return;
        toast.error("Session not found.");
        nav("/app", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [id, nav, replayMode]);

  // Auto-play with fade-in as soon as the dialogue MP3 is ready.
  useEffect(() => {
    if (!audioUrl || !audioRef.current) return undefined;
    const el = audioRef.current;
    const isMorningSession = session?.kind === "morning";
    el.volume = 0;
    const p = el.play();
    if (p && p.catch) {
      p.catch(async () => {
        // Autoplay blocked — never ask for a tap. Try the WebAudio voice path
        // (the mixer context is already unlocked by the navigation gesture);
        // as a last resort, silently retry on the next interaction.
        try {
          if (mixerRef.current) {
            await mixerRef.current.playVoiceUrl(audioUrl, { onEnded: onAudioEnded });
            setPlaying(true);
            return;
          }
        } catch (err) { /* fall through */ }
        retryOnNextGesture(() => { if (audioRef.current) audioRef.current.play().catch(() => { /* ignore */ }); });
      });
    }
    if (mixerRef.current) mixerRef.current.duck(0.9);

    if (isMorningSession) {
      // Morning sessions skip the 1.5s arrival fade — the 15-min wake ramp
      // owns the dialogue volume from 0.05 → 1.0 across fifteen minutes.
      startMorningRampIfNeeded();
      return undefined;
    }

    // Standard arrival fade-in
    const startedAt = Date.now();
    const RAMP_MS = 1500;
    const iv = setInterval(() => {
      const t = Math.min(1, (Date.now() - startedAt) / RAMP_MS);
      if (audioRef.current) audioRef.current.volume = t;
      if (t >= 1) clearInterval(iv);
    }, 40);
    return () => clearInterval(iv);
  }, [audioUrl, session?.kind]);

  // Estimated duration fallback when no real audio
  const wordCount = session?.narration ? session.narration.split(/\s+/).length : 0;
  const estSeconds = Math.max(60, Math.round((wordCount / 130) * 60));
  const effectiveDuration = duration > 0 ? duration : estSeconds;

  // Tear down mixer on unmount
  useEffect(() => {
    return () => {
      if (mixerRef.current) mixerRef.current.stop();
      if (fakeTickRef.current) clearInterval(fakeTickRef.current);
      if (morningRampRef.current) clearInterval(morningRampRef.current);
      if (endFadeTimerRef.current) clearTimeout(endFadeTimerRef.current);
      cueAudiosRef.current.forEach((a) => { try { a.pause(); } catch (e) { /* ignore */ } });
      if (ambientAudioRef.current) {
        try { ambientAudioRef.current.pause(); ambientAudioRef.current.srcObject = null; } catch (e) { /* ignore */ }
      }
      try { stopKeepAlive(); } catch (e) { /* ignore */ }
      clearSessionMetadata();
    };
  }, []);

  // Lock-screen / media-session metadata. Always matches the active character.
  useEffect(() => {
    if (!character) return;
    setSessionMetadata({
      characterName: character.name,
      atmosphereName: atmosphere?.name || session?.atmosphere_name,
      kind: session?.kind,
      artworkUrl: character.image || atmosphere?.image,
    });
    bindMediaSessionControls({
      onPlay: () => {
        userInitiatedPauseRef.current = false;
        if (audioRef.current) audioRef.current.play().catch(() => { /* ignore */ });
      },
      onPause: () => {
        // Explicit lock-screen pause tap — user intent.
        userInitiatedPauseRef.current = true;
        if (audioRef.current) audioRef.current.pause();
      },
    });
  }, [character, atmosphere, session?.kind, session?.atmosphere_name]);

  // Iter 118 — INVOLUNTARY-PAUSE RECOVERY for replay.
  // If the OS suspended the tab or interrupted the audio session, resume
  // as soon as we're allowed. User-initiated pauses are left alone.
  useEffect(() => {
    const attemptRecovery = () => {
      if (userInitiatedPauseRef.current) return;
      const el = audioRef.current;
      if (!el || el.ended) return;
      if (!el.paused) return;
      const p = el.play();
      if (p && p.catch) p.catch(() => { /* retry on next lifecycle event */ });
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") attemptRecovery();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("continua:app-active", attemptRecovery);
    window.addEventListener("focus", attemptRecovery);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("continua:app-active", attemptRecovery);
      window.removeEventListener("focus", attemptRecovery);
    };
  }, []);

  /**
   * Morning wake-up ramp: linearly raise BOTH the dialogue volume and the
   * ambient master gain from 5% → 100% over MORNING_RAMP_SEC. The ramp is
   * scheduled once when audio first starts; it survives subsequent
   * pause/resume because the underlying audio element holds the volume value.
   */
  const MORNING_RAMP_SEC = 15 * 60; // 15 minutes
  const startMorningRampIfNeeded = () => {
    if (!session || session.kind !== "morning") return;
    if (morningRampRef.current) return; // only schedule once
    const el = audioRef.current;
    if (el) el.volume = 0.05;
    // The ambient mixer schedules its own ramp in Web Audio time.
    if (mixerRef.current) mixerRef.current.setMaster(0.55, MORNING_RAMP_SEC);
    const startedAt = Date.now();
    morningRampRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const t = Math.min(1, elapsed / MORNING_RAMP_SEC);
      const vol = 0.05 + (1 - 0.05) * t;
      if (audioRef.current) audioRef.current.volume = vol;
      if (t >= 1) {
        clearInterval(morningRampRef.current);
        morningRampRef.current = null;
      }
    }, 1500);
  };

  const startAmbient = async () => {
    if (!ambientEnabled || !manifest) return;
    if (!mixerRef.current) mixerRef.current = new AmbientMixer();
    let soundPlan = manifest.layers.sound_plan;
    // Iter 115 — Morning ambient progression: the fire is NOT burning when she
    // wakes. Hold fire loops back until her narrated firewood action happens
    // (or the 40% safety net). The cabin evolves instead of starting complete.
    const hasFirewoodCue = Array.isArray(session?.action_cues) && session.action_cues.some((c) => c.category === "firewood");
    if (session?.kind === "morning" && hasFirewoodCue && soundPlan?.ambient_loops?.assets?.length) {
      const isFire = (a) => /fire|crackle|ember/i.test(`${a.category || ""} ${a.variant || ""} ${a.file || ""} ${a.storage?.url || ""}`);
      const held = soundPlan.ambient_loops.assets.filter(isFire);
      if (held.length) {
        heldFireLoopsRef.current = held;
        fireStartedRef.current = false;
        soundPlan = {
          ...soundPlan,
          ambient_loops: { ...soundPlan.ambient_loops, assets: soundPlan.ambient_loops.assets.filter((a) => !isFire(a)) },
        };
      }
    }
    await mixerRef.current.start({
      environment: manifest.layers.environment,
      soundPlan,
      environmentState: manifest.layers.environment?.environment_state,
    });
    if (audioUrl) mixerRef.current.duck(0.9);
  };

  const stopAmbient = async () => {
    if (mixerRef.current) {
      await mixerRef.current.stop();
      mixerRef.current = null;
    }
  };

  const onTogglePlay = async () => {
    // Voice riding the WebAudio fallback path — mixer controls everything.
    if (mixerRef.current && mixerRef.current._voiceSource) {
      if (playing) {
        userInitiatedPauseRef.current = true;
        await mixerRef.current.pause();
        setPlaying(false);
      } else {
        userInitiatedPauseRef.current = false;
        await mixerRef.current.resume();
        setPlaying(true);
      }
      return;
    }
    // Real audio path
    if (audioUrl && audioRef.current) {
      if (playing) {
        userInitiatedPauseRef.current = true;
        audioRef.current.pause();
      } else {
        userInitiatedPauseRef.current = false;
        if (audioRef.current.ended) {
          audioRef.current.currentTime = 0;
        }
        try {
          await startAmbient();
          startMorningRampIfNeeded();
          await audioRef.current.play();
        } catch (e) {
          // Blocked inside a gesture is near-impossible; self-heal silently.
          retryOnNextGesture(() => { if (audioRef.current) audioRef.current.play().catch(() => { /* ignore */ }); });
        }
      }
      return;
    }
    // No-audio path (TTS not configured) — simulate progress + ambient bed
    if (!playing) {
      userInitiatedPauseRef.current = false;
      await startAmbient();
      startMorningRampIfNeeded();
      if (progress >= 1) setProgress(0);
      setPlaying(true);
      fakeTickRef.current = setInterval(() => {
        setProgress((p) => {
          const n = p + (1 / effectiveDuration) * 0.5;
          if (n >= 1) { setPlaying(false); return 1; }
          return n;
        });
      }, 500);
    } else {
      userInitiatedPauseRef.current = true;
      setPlaying(false);
      if (fakeTickRef.current) clearInterval(fakeTickRef.current);
      if (mixerRef.current) mixerRef.current.pause();
    }
  };

  // Real audio element events
  const onAudioPlay = () => {
    setPlaying(true);
    // If the user replays after the session ended, cancel the drift-to-silence.
    if (endFadeTimerRef.current) { clearTimeout(endFadeTimerRef.current); endFadeTimerRef.current = null; }
    // Resume ambient with the dialogue — they belong to the same session.
    if (mixerRef.current) mixerRef.current.resume();
  };
  const onAudioPause = () => {
    setPlaying(false);
    // Iter 118 — only follow the audio element into pause if this was the
    // user's explicit intent. An involuntary OS/browser pause (iOS
    // suspension, tab hidden, audio-session interruption) leaves the mixer
    // running and immediately retries playback. Continua's core promise:
    // playback stops only when the user asks it to.
    if (!userInitiatedPauseRef.current) {
      if (audioRef.current && !audioRef.current.ended) {
        const p = audioRef.current.play();
        if (p && p.catch) p.catch(() => { /* recovery loop will retry on foreground */ });
      }
      return;
    }
    // Pause ambient with the dialogue — no orphaned white noise while the
    // companion is silent.
    if (mixerRef.current) mixerRef.current.pause();
  };
  const onAudioEnded = async () => {
    setPlaying(false);
    setProgress(1);
    // Presence mode: companion stays nearby, ambient unducks back up
    setPresenceMode(true);
    if (mixerRef.current) {
      mixerRef.current.unduck(1.0);
      // Rest/Adventure endings drift gently to silence instead of cutting off.
      // Morning sessions own their own long wake ramp — never fade them down.
      if (session?.kind !== "morning") {
        if (endFadeTimerRef.current) clearTimeout(endFadeTimerRef.current);
        endFadeTimerRef.current = setTimeout(() => {
          if (mixerRef.current) mixerRef.current.fadeToSilence(300);
        }, 90000);
      }
    }
    toast("They're still nearby.", { duration: 5000 });
  };
  const onTimeUpdate = () => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    setDuration(el.duration);
    setProgress(el.currentTime / el.duration);
    fireCueIfDue(el);
  };

  // Iter 115 — action cue engine: her narrated physical actions (firewood,
  // tea pouring, blanket, coiling) become real SFX at the moment she says them.
  const playedCuesRef = useRef(new Set());
  const cueAudiosRef = useRef([]);
  const heldFireLoopsRef = useRef([]);
  const fireStartedRef = useRef(false);

  const startHeldFireLoops = () => {
    if (fireStartedRef.current || !heldFireLoopsRef.current.length) return;
    fireStartedRef.current = true;
    const m = mixerRef.current;
    if (!m) return;
    heldFireLoopsRef.current.forEach((a) => {
      m.playSampleLoop(a.storage?.url, a.target_gain || 0.3).catch(() => { /* ignore */ });
    });
  };

  const fireCueIfDue = (el) => {
    const cues = session?.action_cues;
    if (!el?.duration) return;
    const ratio = el.currentTime / el.duration;
    // Safety net: if no firewood cue ever fires, the fire still wakes by 40%.
    if (ratio > 0.4) startHeldFireLoops();
    if (!Array.isArray(cues) || !cues.length) return;
    cues.forEach((cue, i) => {
      if (playedCuesRef.current.has(i)) return;
      const at = typeof cue.text_offset_ratio === "number" ? cue.text_offset_ratio : null;
      if (at === null || ratio < at) return;
      playedCuesRef.current.add(i);
      if (cue.sfx_url) {
        try {
          const a = new Audio(cue.sfx_url);
          a.volume = Math.max(0, Math.min(1, cue.volume ?? 0.3));
          cueAudiosRef.current.push(a);
          a.play().catch(() => { /* ignore */ });
        } catch (e) { /* ignore */ }
      }
      // Morning ambient progression: feeding the fire brings the crackle alive.
      if (cue.category === "firewood") setTimeout(startHeldFireLoops, 2500);
    });
  };

  const exitPresence = async () => {
    setPresenceMode(false);
    await stopAmbient();
  };

  const toggleAmbient = async () => {
    const next = !ambientEnabled;
    setAmbientEnabled(next);
    if (!next) {
      await stopAmbient();
    } else if (playing) {
      await startAmbient();
    }
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const t = Math.max(0, Math.min(1, x));
    setProgress(t);
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = t * audioRef.current.duration;
    }
  };

  const fmtTime = (t) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!session) {
    return <div className="min-h-screen flex items-center justify-center text-violet-300/60">Loading…</div>;
  }
  const isMorning = session.kind === "morning";

  return (
    <div className="min-h-screen relative text-violet-50">
      <div className="absolute inset-0 grain-overlay pointer-events-none" />
      <TopBar onOpenSettings={() => setDrawerOpen(true)} />

      <motion.div
        className="relative z-10 max-w-3xl mx-auto px-6 sm:px-8 pt-3 pb-60"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 0.61, 0.36, 1] }}
      >
        <button
          type="button" onClick={async () => { await stopAmbient(); nav("/app"); }}
          data-testid="session-back-button"
          className="inline-flex items-center gap-2 text-sm text-violet-300/60 hover:text-violet-50 mb-10 transition-soft"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-10 fade-rise" data-testid="session-hero-header">
          {session.memory_title && (
            <div className="text-violet-200/90 mb-3 flex items-center gap-2 font-serif-display italic" data-testid="session-memory-title">
              <BookmarkPlus className="w-3.5 h-3.5" /> {session.memory_title}
            </div>
          )}
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mb-3" data-testid="session-hero-title">
            {character?.name} <span className="text-violet-300/60 italic">at the</span> {atmosphere?.name?.toLowerCase()}
          </h1>
          {audioStatus === "rendering" && (
            <p className="text-violet-300/70 text-xs flex items-center gap-2 mt-2" data-testid="audio-status-rendering">
              <Sparkles className="w-3 h-3 animate-pulse" /> Warming her voice…
            </p>
          )}
          {audioStatus === "failed" && (
            <p className="text-violet-300/70 text-xs mt-2" data-testid="audio-status-failed">
              {replayMode
                ? "This night's voice has drifted from memory — the world is still here."
                : "Voice synthesis wasn't available — the world is still here."}
            </p>
          )}
          {isMorning && audioStatus === "ready" && (
            <p className="text-violet-300/70 text-xs mt-2 flex items-center gap-2" data-testid="morning-ramp-hint">
              <Sun className="w-3 h-3" /> Wake gently — the sound will rise over fifteen minutes.
            </p>
          )}
        </div>

        {/* Presence mode message after audio ends */}
        {presenceMode && (
          <div className="mt-12 glass rounded-2xl p-6 fade-rise" data-testid="presence-mode-card">
            <div className="text-xs uppercase tracking-[0.28em] text-violet-300/90 mb-2 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Presence
            </div>
            <p className="text-violet-50/90 text-[15px] leading-relaxed">
              {character?.name} has not gone anywhere. The {atmosphere?.name?.toLowerCase()} keeps moving softly around you. Stay as long as you would like.
            </p>
            <button
              type="button" onClick={exitPresence}
              data-testid="presence-mode-exit"
              className="mt-5 text-sm rounded-full px-5 py-2 border border-violet-300/30 text-violet-200 hover:border-violet-300/60 hover:text-violet-50 transition-soft"
            >
              Quiet now
            </button>
          </div>
        )}

        {/* Iter 87 — the reflection panel moved into the live session view
            (WaitingRoom) so it appears directly beneath the completed session. */}
      </motion.div>

      {/* Hidden audio element drives playback when TTS audio is ready */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          onPlay={onAudioPlay}
          onPause={onAudioPause}
          onEnded={onAudioEnded}
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={onTimeUpdate}
          data-testid="dialogue-audio"
        />
      )}
      {/* Hidden ambient mirror — keeps WebAudio bed alive under the lock screen */}
      <audio
        ref={ambientAudioRef}
        autoPlay
        playsInline
        style={{ display: "block", position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0, pointerEvents: "none" }}
        data-testid="session-ambient-audio"
      />

      {/* Player */}
      <BottomNav bottom="6.25rem" />
      <SettingsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div
        className="fixed bottom-0 left-0 right-0 z-20 pt-12 px-6 pointer-events-none"
        style={{ paddingBottom: "calc(1.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="max-w-3xl mx-auto pointer-events-auto">
          <div className="glass-strong rounded-full px-5 py-3 flex items-center gap-5">
            <button
              type="button" onClick={onTogglePlay}
              disabled={audioStatus === "rendering"}
              data-testid="audio-play-button"
              aria-label={playing ? "Pause" : "Play"}
              className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-soft ${playing ? "bg-violet-300 text-indigo-950" : "bg-violet-200 text-indigo-950 hover:bg-violet-100"} disabled:opacity-50`}
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-violet-300/70 mb-1.5">
                <span>{fmtTime(progress * effectiveDuration)}</span>
                <span>{fmtTime(effectiveDuration)}</span>
              </div>
              <div
                role="slider" aria-valuenow={Math.round(progress * 100)}
                onClick={seek}
                data-testid="audio-progress-bar"
                className="h-1 bg-violet-300/15 rounded-full overflow-hidden cursor-pointer"
              >
                <div className="h-full bg-violet-300 transition-all duration-300 ease-linear" style={{ width: `${progress * 100}%` }} />
              </div>
            </div>
            <button
              type="button" onClick={toggleAmbient}
              data-testid="ambient-toggle-button"
              title={ambientEnabled ? "Mute atmosphere" : "Enable atmosphere"}
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-violet-200 hover:bg-violet-300/15 transition-soft"
            >
              {ambientEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
